import axiosClient from '../clients/axiosClient';

// Types based on API documentation
export interface Condition {
  id: number;
  name: string;
  body_system_ids: number[];
  description: string;
  biomarker_ids: number[];
  mri_condition_ids?: number[];
  created_at: string;
  updated_at: string;
  status: string;
}

export interface BodySystem {
  id: number;
  title: string;
}

export interface Biomarker {
  id: number;
  name: string;
}

/** Associated FBS (MRI) condition nested on condition list/detail responses */
export interface AssociatedFbsCondition {
  id: number;
  condition_description: string;
  radiology_organ_name?: string;
  radiologist_body_system_name?: string;
}

export interface ConditionWithDetails {
  id: number;
  name: string;
  body_systems: BodySystem[];
  description: string;
  biomarkers: Biomarker[];
  mri_conditions?: AssociatedFbsCondition[];
  created_at: string;
  updated_at: string;
}

export interface CreateConditionRequest {
  name: string | null;
  description: string | null;
  body_system_ids: number[];
  biomarker_ids: number[];
  mri_condition_ids?: number[];
}

export interface UpdateConditionRequest {
  name?: string | null;
  description?: string | null;
  body_system_ids?: number[];
  biomarker_ids?: number[];
  mri_condition_ids?: number[];
}

export const getFbsConditionLabel = (
  condition: Pick<AssociatedFbsCondition, 'condition_description' | 'radiology_organ_name'>
): string => {
  if (condition.radiology_organ_name) {
    return `${condition.condition_description} (${condition.radiology_organ_name})`;
  }
  return condition.condition_description;
};

export interface ConditionsListResponse {
  conditions: ConditionWithDetails[];
  total_count: number;
  limit: number;
  offset: number;
  status: string;
}

export interface ConditionResponse {
  id: number;
  name: string;
  body_system_ids: number[];
  description: string;
  biomarker_ids: number[];
  mri_condition_ids?: number[];
  created_at: string;
  updated_at: string;
  status: string;
}

export interface ErrorResponse {
  description: string;
}

export interface ValidationErrorResponse {
  detail: Array<{
    loc: (string | number)[];
    msg: string;
    type: string;
  }>;
}

// API Functions

/**
 * Get list of conditions with pagination and search
 */
export const getConditionsList = async (
  searchQuery?: string,
  limit: number = 10,
  offset: number = 0,
  sortField?: string,
  sortOrder: 'asc' | 'desc' = 'asc'
): Promise<ConditionsListResponse> => {
  const params = new URLSearchParams();
  if (searchQuery) params.append('search_query', searchQuery);
  if (sortField) params.append('sort_field', sortField);
  params.append('sort_order', sortOrder);
  params.append('limit', limit.toString());
  params.append('offset', offset.toString());

  const response = await axiosClient.get(`/api/v1/conditions/list/?${params.toString()}`);
  return response.data;
};

/**
 * Get a specific condition by ID
 */
export const getConditionById = async (conditionId: number): Promise<ConditionWithDetails> => {
  const response = await axiosClient.get(`/api/v1/conditions/`, {
    params: { condition_id: conditionId }
  });
  return response.data;
};

/**
 * Create a new condition
 */
export const createCondition = async (conditionData: CreateConditionRequest): Promise<ConditionResponse> => {
  const response = await axiosClient.post('/api/v1/conditions/', conditionData);
  return response.data;
};

/**
 * Update an existing condition by ID
 */
export const updateCondition = async (
  conditionId: number,
  conditionData: UpdateConditionRequest
): Promise<ConditionResponse> => {
  const requestBody = {
    ...conditionData,
    condition_id: conditionId
  };
  const response = await axiosClient.patch(`/api/v1/conditions/`, requestBody);
  return response.data;
};

/**
 * Delete a condition by ID
 */
export const deleteCondition = async (conditionId: number): Promise<{ condition_id: number; status: string; message: string }> => {
  const response = await axiosClient.delete(`/api/v1/conditions/`, {
    data: { condition_id: conditionId }
  });
  return response.data;
};
